import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './Modal.jsx';
import { Icon } from './Icon.jsx';
import { PdfPreview } from './PdfPreview.jsx';
import { usePdf } from '../store/pdfStore.js';
import { useLetters } from '../store/lettersStore.js';
import { useT } from '../i18n/index.js';
import { downloadBlob } from '../utils/storage.js';
import { generateLetterPdf } from '../utils/pdfOps.js';

// Generated previews of saved-but-not-exported letters are cached.
const previewCache = new Map();

function formatSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(ts) {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return '';
  }
}

export function HistoryModal({ open, onClose, onLoadLetter }) {
  const t = useT();
  const exports = usePdf((s) => s.exports);
  const removeExport = usePdf((s) => s.removeExport);
  const clearExports = usePdf((s) => s.clearExports);
  const getExportBlob = usePdf((s) => s.getExportBlob);
  const templates = useLetters((s) => s.templates);
  const loadTemplate = useLetters((s) => s.loadTemplate);
  const deleteTemplate = useLetters((s) => s.deleteTemplate);

  const [selectedKey, setSelectedKey] = useState(null);
  const [genBlob, setGenBlob] = useState(null);
  const [generating, setGenerating] = useState(false);
  const rowRefs = useRef({});
  const reqRef = useRef(0);

  // Unified list — every kind of history in one place:
  //  - export entries (edited PDFs + generated letters)
  //  - letter templates that were saved but never generated (no export
  //    references their id), so nothing is hidden and nothing duplicated.
  const entries = useMemo(() => {
    const exportedTplIds = new Set(
      exports
        .filter((e) => e.source === 'letters' && e.templateId)
        .map((e) => e.templateId)
    );
    const exportEntries = exports.map((e) => ({
      kind: 'export',
      key: e.id,
      name: e.name,
      date: e.date || 0,
      source: e.source === 'letters' ? 'letters' : 'edit',
      raw: e,
    }));
    const templateEntries = templates
      .filter((tp) => !exportedTplIds.has(tp.id))
      .map((tp) => ({
        kind: 'template',
        key: `tpl:${tp.id}`,
        name: tp.name,
        date: tp.date || 0,
        source: 'letters',
        raw: tp,
      }));
    return [...exportEntries, ...templateEntries].sort(
      (a, b) => (b.date || 0) - (a.date || 0)
    );
  }, [exports, templates]);

  // Keep a valid selection — default to the first (newest) entry.
  useEffect(() => {
    if (!open) return;
    if (!entries.some((e) => e.key === selectedKey)) {
      setSelectedKey(entries[0]?.key ?? null);
    }
  }, [open, entries, selectedKey]);

  // Up/Down arrows move the selection through the list.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!entries.length) return;
      e.preventDefault();
      const i = entries.findIndex((x) => x.key === selectedKey);
      const ni =
        e.key === 'ArrowDown'
          ? Math.min(entries.length - 1, (i < 0 ? -1 : i) + 1)
          : Math.max(0, (i < 0 ? 0 : i) - 1);
      setSelectedKey(entries[ni].key);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, entries, selectedKey]);

  const resolvedKey = entries.some((e) => e.key === selectedKey)
    ? selectedKey
    : entries[0]?.key ?? null;

  useEffect(() => {
    rowRefs.current[resolvedKey]?.scrollIntoView({ block: 'nearest' });
  }, [resolvedKey]);

  const entry = entries.find((e) => e.key === resolvedKey) || null;
  const isExport = entry?.kind === 'export';
  const isTemplate = entry?.kind === 'template';
  const isLetter = entry?.source === 'letters';

  // Stored blobs for export entries.
  const exportedBlob = isExport ? getExportBlob(entry.raw.id, 'exported') : null;
  const originalBlob = isExport ? getExportBlob(entry.raw.id, 'original') : null;
  // Template linked to a letter-export (for the pre-fill action).
  const linkedTemplateId = isExport && isLetter ? entry.raw.templateId : null;
  const linkedTemplate = linkedTemplateId
    ? templates.find((x) => x.id === linkedTemplateId)
    : null;

  // Generate (and cache) the preview PDF for saved-but-not-generated
  // letter templates. Export entries use their stored blob instead.
  useEffect(() => {
    const req = ++reqRef.current;
    if (!open || !entry || entry.kind !== 'template') {
      setGenBlob(null);
      setGenerating(false);
      return;
    }
    const tp = entry.raw;
    const cacheKey = `${tp.id}:${tp.date || ''}`;
    const cached = previewCache.get(cacheKey);
    if (cached) {
      setGenBlob(cached);
      setGenerating(false);
      return;
    }
    setGenerating(true);
    setGenBlob(null);
    let alive = true;
    (async () => {
      try {
        const blob = await generateLetterPdf({
          ...tp,
          signature: tp.signature || null,
        });
        if (!alive || req !== reqRef.current) return;
        previewCache.set(cacheKey, blob);
        setGenBlob(blob);
      } catch {
        /* preview is best-effort */
      } finally {
        if (alive && req === reqRef.current) setGenerating(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, entry]);

  const previewBlob = isTemplate ? genBlob : exportedBlob;

  const downloadExport = (which) => {
    if (!isExport) return;
    const blob = getExportBlob(entry.raw.id, which);
    if (!blob) return;
    const name =
      which === 'original'
        ? entry.raw.originalName || `original-${entry.name}`
        : entry.name;
    downloadBlob(blob, name);
  };

  const downloadTemplate = () => {
    if (!genBlob) return;
    const name = entry.name.toLowerCase().endsWith('.pdf')
      ? entry.name
      : `${entry.name}.pdf`;
    downloadBlob(genBlob, name);
  };

  // Open a letter (export-linked template or a bare template) in the
  // letter-template editor.
  const prefill = (id) => {
    if (!id) return;
    loadTemplate(id);
    onClose?.();
    onLoadLetter?.();
  };

  const remove = () => {
    if (!entry) return;
    if (entry.kind === 'export') removeExport(entry.raw.id);
    else deleteTemplate(entry.raw.id);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('history.title')}
      size="lg"
      footer={
        <>
          <button className="btn sm danger" onClick={clearExports} disabled={!exports.length}>
            <Icon name="trash" size={14} /> {t('history.clear_all')}
          </button>
          <button className="btn primary sm" onClick={onClose}>{t('modal.close')}</button>
        </>
      }
    >
      {entries.length === 0 ? (
        <div className="empty">{t('history.empty')}</div>
      ) : (
        <div className="history-layout">
          <div className="history-rows">
            {entries.map((e) => {
              const letter = e.source === 'letters';
              return (
                <button
                  key={e.key}
                  ref={(el) => { rowRefs.current[e.key] = el; }}
                  className={`hist-row ${e.key === resolvedKey ? 'selected' : ''}`}
                  onClick={() => setSelectedKey(e.key)}
                >
                  <Icon name={letter ? 'text' : 'doc'} size={15} />
                  <span className="hist-row-name" title={e.name}>{e.name}</span>
                  <span className={`tag ${letter ? 'tag-letters' : 'tag-edit'}`}>
                    {letter ? t('history.source_letters') : t('history.source_edit')}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="history-detail">
            {entry ? (
              <>
                <PdfPreview
                  blob={previewBlob}
                  loading={isTemplate && generating}
                  loadingLabel={t('history.preview_loading')}
                  emptyLabel={t('history.expired')}
                />
                <div className="history-detail-meta">
                  <div className="hist-detail-name" title={entry.name}>{entry.name}</div>
                  <div className="history-meta">
                    <span>{formatDate(entry.date)}</span>
                    {isExport && (
                      <>
                        <span>·</span>
                        <span>
                          {entry.raw.pages} {entry.raw.pages > 1 ? 'pages' : 'page'}
                        </span>
                        <span>·</span>
                        <span>{formatSize(entry.raw.size)}</span>
                      </>
                    )}
                    {isTemplate && entry.raw.subject && (
                      <>
                        <span>·</span>
                        <span>{entry.raw.subject}</span>
                      </>
                    )}
                  </div>
                  {isExport && entry.raw.hash && (
                    <div className="history-hash" title="SHA-256">
                      {entry.raw.hash.slice(0, 32)}…
                    </div>
                  )}
                </div>
                <div className="history-detail-actions">
                  {isExport ? (
                    <button
                      className="btn sm primary"
                      onClick={() => downloadExport('exported')}
                      disabled={!exportedBlob}
                    >
                      <Icon name="download" size={14} /> {t('history.download_exported')}
                    </button>
                  ) : (
                    <button
                      className="btn sm primary"
                      onClick={downloadTemplate}
                      disabled={!genBlob}
                    >
                      <Icon name="download" size={14} /> {t('history.download_exported')}
                    </button>
                  )}
                  {isExport && !isLetter && (
                    <button
                      className="btn sm"
                      onClick={() => downloadExport('original')}
                      disabled={!originalBlob}
                    >
                      <Icon name="download" size={14} /> {t('history.download_original')}
                    </button>
                  )}
                  {isExport && isLetter && (
                    <button
                      className="btn sm"
                      onClick={() => prefill(linkedTemplateId)}
                      disabled={!linkedTemplate}
                    >
                      <Icon name="text" size={14} /> {t('history.prefill')}
                    </button>
                  )}
                  {isTemplate && (
                    <button className="btn sm" onClick={() => prefill(entry.raw.id)}>
                      <Icon name="text" size={14} /> {t('history.prefill')}
                    </button>
                  )}
                  <button className="btn sm danger" onClick={remove}>
                    <Icon name="trash" size={14} /> {t('history.remove')}
                  </button>
                </div>
              </>
            ) : (
              <div className="empty">{t('history.select_hint')}</div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
